<template>
  <div class="linked-customers-box">
    <div class="nav linked-customers-card">
      <div class="flex justify-between items-center mb-3">
        <h2>
          {{
            customerName ? `${customerName} — Web Customers` : "Web Customers"
          }}
        </h2>
        <button @click="showAddModal = true" class="btn-success-button">
          + Link Customer
        </button>
      </div>

      <!-- Add/Link Customer Modal -->
      <InfoautoAddLinkedCustomerModal
        v-model="showAddModal"
        :infoautoId="customerId"
        @customerLinked="onCustomerLinked"
      />

      <!-- Loading State -->
      <div v-if="loading" class="message-box">
        Loading linked customers...
      </div>

      <!-- Error State -->
      <div v-if="error && !loading" class="message-box warning">
        {{ error }}
      </div>

      <!-- No Customers State -->
      <div
        v-if="!loading && !error && linkedCustomers.length === 0"
        class="message-box"
      >
        No web customers linked to this infoauto ID. Click "Link Customer" to
        add one.
      </div>

      <!-- Customers Table -->
      <div v-if="!loading && linkedCustomers.length > 0">
        <!-- Desktop table -->
        <table class="linked-table desktop-only">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="customer in linkedCustomers" :key="customer.id">
              <td>{{ customer.name }} {{ customer.surname }}</td>
              <td>{{ customer.email }}</td>
              <td class="activity-cell">{{ activityText(customer) }}</td>
              <td class="actions-cell">
                <button
                  @click="editCustomer(customer.id)"
                  class="icon-btn"
                  title="Edit"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                    />
                  </svg>
                </button>
                <button
                  @click="openInNewTab(customer)"
                  class="icon-btn"
                  title="Open in new tab"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                    />
                    <path
                      d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                    />
                  </svg>
                </button>
                <button
                  @click="unlinkCustomer(customer.id)"
                  class="icon-btn danger"
                  title="Unlink"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile cards -->
        <div class="mobile-only">
          <div
            v-for="customer in linkedCustomers"
            :key="customer.id"
            class="customer-card"
          >
            <div class="customer-card-info">
              <span class="customer-card-name"
                >{{ customer.name }} {{ customer.surname }}</span
              >
              <span class="customer-card-email">{{ customer.email }}</span>
              <span class="customer-card-email">{{
                activityText(customer)
              }}</span>
            </div>
            <div class="customer-card-actions">
              <button
                @click="editCustomer(customer.id)"
                class="icon-btn"
                title="Edit"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                  />
                </svg>
              </button>
              <button
                @click="openInNewTab(customer)"
                class="icon-btn"
                title="Open in new tab"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                  />
                  <path
                    d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                  />
                </svg>
              </button>
              <button
                @click="unlinkCustomer(customer.id)"
                class="icon-btn danger"
                title="Unlink"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Unlinked web users that look like users of this customer -->
      <div v-if="!loading && suggestedCustomers.length > 0" class="suggested">
        <h3>{{ $t("message.infoauto_link.suggested_title") }}</h3>
        <p class="suggested-hint">
          {{ $t("message.infoauto_link.suggested_hint") }}
        </p>
        <div
          v-for="customer in suggestedCustomers"
          :key="customer.id"
          class="suggested-row"
        >
          <div class="customer-card-info">
            <button
              class="suggested-name"
              @click="editCustomer(customer.id, customer)"
            >
              {{ customer.name }} {{ customer.surname }}
            </button>
            <span class="customer-card-email">{{ customer.email }}</span>
            <div class="chips">
              <span
                v-for="reason in customer.reasons"
                :key="reason"
                class="chip"
                :class="`chip-${reason}`"
              >
                {{ reasonLabel(reason, customer) }}
              </span>
            </div>
          </div>
          <div class="customer-card-actions">
            <button
              class="btn-success-button"
              :disabled="processingId === customer.id"
              @click="decideSuggestion(customer, 'accepted')"
            >
              {{ $t("message.infoauto_link.connect") }}
            </button>
            <button
              class="btn-dark-button"
              :disabled="processingId === customer.id"
              @click="decideSuggestion(customer, 'rejected')"
            >
              {{ $t("message.infoauto_link.reject") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getInfoautoWebCustomers,
  linkCustomerToInfoauto,
  decideInfoautoLink
} from "@/api/store/CustomersService.js";
import InfoautoAddLinkedCustomerModal from "./InfoautoAddLinkedCustomerModal.vue";

export default {
  components: {
    InfoautoAddLinkedCustomerModal
  },
  props: {
    customerId: {
      type: String,
      required: true
    },
    customerName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      linkedCustomers: [],
      suggestedCustomers: [],
      processingId: null,
      loading: false,
      error: null,
      showAddModal: false
    };
  },
  watch: {
    customerId: {
      handler(val) {
        if (val) {
          this.fetchLinkedCustomers();
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetchLinkedCustomers() {
      this.loading = true;
      this.error = null;
      try {
        const response = await getInfoautoWebCustomers(this.customerId);
        this.linkedCustomers = response.linked || [];
        this.suggestedCustomers = response.suggested || [];
      } catch (err) {
        this.error = "Failed to load linked customers. Please try again.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    editCustomer(customerId, customer) {
      this.$emit(
        "editCustomer",
        customerId,
        customer || this.linkedCustomers.find(c => c.id === customerId)
      );
    },

    activityText(customer) {
      const orders = Number(customer.ordersTotal) || 0;
      const parts = [
        orders > 0
          ? `${orders} orders · last ${this.formatDate(customer.lastOrder)}`
          : "No orders"
      ];
      if (Number(customer.cartItems) > 0) {
        parts.push(`${customer.cartItems} in cart`);
      }
      return parts.join(" · ");
    },

    reasonLabel(reason, customer) {
      if (reason === "orders") {
        return this.$t("message.infoauto_link.reason_orders", {
          count: customer.ordersBilled
        });
      }
      return this.$t(`message.infoauto_link.reason_${reason}`);
    },

    async decideSuggestion(customer, decision) {
      const userName = `${customer.name} ${customer.surname}`.trim();
      const account = this.customerName || this.customerId;
      if (
        decision === "accepted" &&
        !confirm(
          this.$t("message.infoauto_link.confirm_connect", {
            user: userName || customer.email,
            account
          })
        )
      ) {
        return;
      }
      this.processingId = customer.id;
      try {
        await decideInfoautoLink(
          customer.id,
          this.customerId,
          decision,
          customer.reasons
        );
        if (decision === "accepted") {
          await this.fetchLinkedCustomers();
        } else {
          this.suggestedCustomers = this.suggestedCustomers.filter(
            c => c.id !== customer.id
          );
        }
      } catch (err) {
        this.$toast.open({
          message:
            err.response?.data?.message ||
            this.$t("message.infoauto_link.error"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.processingId = null;
      }
    },

    openInNewTab(customer) {
      const { href } = this.$router.resolve({
        name: "InfoautoCustomersPage",
        query: {
          tab: "web",
          search: customer.email,
          web_open: customer.id
        }
      });
      window.open(href, "_blank");
    },

    async unlinkCustomer(customerId) {
      if (!confirm("Are you sure you want to unlink this customer?")) {
        return;
      }

      try {
        await linkCustomerToInfoauto(customerId, "");
        this.linkedCustomers = this.linkedCustomers.filter(
          c => c.id !== customerId
        );
        this.$notify({
          type: "success",
          title: "Success",
          message: "Customer unlinked successfully"
        });
      } catch (err) {
        this.$notify({
          type: "error",
          title: "Error",
          message: "Failed to unlink customer"
        });
        console.error(err);
      }
    },

    onCustomerLinked() {
      this.showAddModal = false;
      this.fetchLinkedCustomers();
      this.$notify({
        type: "success",
        title: "Success",
        message: "Customer linked successfully"
      });
    },

    getPriceLevelClass(priceLevel) {
      const map = {
        online: "info",
        repair_shop: "warning",
        wholesale: "dark",
        precio4: "secondary",
        precio5: "secondary",
        precio6: "secondary"
      };
      return map[priceLevel] || "secondary";
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.linked-customers-box {
  width: 100%;
  font-size: 16px;

  h2 {
    font-weight: bold;
    color: #000;
    padding-bottom: 4px;
  }
}

.linked-customers-card {
  @apply p-4 border rounded-md;
  width: 100%;
}

.desktop-only {
  display: table;
}
.mobile-only {
  display: none;
}

@media (max-width: 640px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
}

.linked-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background-color: #f4f4f4;
    border-bottom: 2px solid #ddd;
    th {
      padding: 10px 12px;
      text-align: left;
      font-weight: 600;
      color: #333;
    }
  }

  tbody {
    tr:hover {
      background-color: #f9f9f9;
    }
    td {
      padding: 10px 12px;
      border-bottom: 1px solid #eee;
      vertical-align: middle;
    }
    tr:last-child td {
      border-bottom: none;
    }
  }

  .actions-cell {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }
}

// Mobile cards
.customer-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;

  &:last-child {
    margin-bottom: 0;
  }
}

.customer-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.customer-card-name {
  font-weight: 600;
  font-size: 14px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customer-card-email {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customer-card-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
}

td.activity-cell {
  font-size: 13px;
  color: #6b7280;
}

.suggested {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #d1d5db;

  h3 {
    font-weight: 600;
    font-size: 15px;
  }
}

.suggested-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 8px;
}

.suggested-row {
  @extend .customer-card;
  flex-wrap: wrap;
  gap: 8px;
}

.suggested-name {
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #1d4ed8;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.chip {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #374151;
  &.chip-email {
    background: #dcfce7;
    color: #166534;
  }
  &.chip-orders {
    background: #dbeafe;
    color: #1e40af;
  }
  &.chip-phone {
    background: #fef3c7;
    color: #92400e;
  }
}

// Icon buttons
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #e5e7eb;
  }

  &.danger {
    border-color: #fca5a5;
    background: #fff5f5;
    color: #dc2626;
    &:hover {
      background: #fee2e2;
    }
  }
}
</style>
